import { useCallback, useEffect, useRef, useState } from "react";

// Left offsets for frozen (pinned) columns, with a hard cap on how much of the grid
// the frozen block may consume.
//
// Why the cap: pinned columns are position:sticky at increasing left offsets, so the
// frozen block eats horizontal space that can no longer scroll. Pin enough columns and
// the scrollable region collapses to nothing — the grid becomes unusable and the user
// cannot see the columns they were trying to compare against. The cap means the common
// case (1-3 pins) freezes as expected, while over-pinning degrades gracefully: columns
// past the cap still lead the grid (they were reordered by applyPins) but scroll
// normally instead of stranding the viewport.
//
// Offsets are measured from the REAL rendered header cells rather than assumed, because
// column widths depend entirely on content (a policy number and an amount are nowhere
// near the same width). Measured after layout via useLayoutEffect-style rAF, and
// re-measured on resize and whenever the pinned set or column set changes.

export const FROZEN_WIDTH_FRACTION = 0.5;

export interface FrozenInfo {
  /** Ref to attach to the scrolling container that wraps the table. */
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Sticky left offset in px for a pinned column, or undefined if it isn't frozen. */
  offsetFor: (colKey: string) => number | undefined;
  /** True once the frozen block hit the cap (some pins reordered but did not freeze). */
  capped: boolean;
}

/**
 * @param pinnedInOrder pinned column keys, in the left-to-right order they render
 * @param deps          changes that can alter column widths (frame/column identity)
 * @param reservedLeft  px already frozen ahead of the pinned block (e.g. a leading
 *                      select column), charged against the same budget
 */
export function useFrozenOffsets(pinnedInOrder: string[], deps: unknown, reservedLeft = 0): FrozenInfo {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [offsets, setOffsets] = useState<Map<string, number>>(new Map());
  const [capped, setCapped] = useState(false);
  const signature = pinnedInOrder.join(" ");

  const measure = useCallback(() => {
    const root = scrollRef.current;
    if (!root || pinnedInOrder.length === 0) {
      setOffsets((prev) => (prev.size ? new Map() : prev));
      setCapped(false);
      return;
    }
    // Header cells carry data-col-key; the first row of the table is authoritative
    // for widths (all body cells in a column share it).
    const cells = root.querySelectorAll<HTMLElement>("thead th[data-col-key]");
    const widths = new Map<string, number>();
    cells.forEach((el) => {
      const k = el.dataset.colKey;
      if (k) widths.set(k, el.getBoundingClientRect().width);
    });
    // A leading frozen select column eats the same budget the pins draw from.
    const budget = root.clientWidth * FROZEN_WIDTH_FRACTION - reservedLeft;
    const next = new Map<string, number>();
    let run = 0;
    let hitCap = false;
    for (const key of pinnedInOrder) {
      const w = widths.get(key);
      if (w === undefined) continue;      // pinned but not present in this frame
      // Always freeze the first pinned column even if it alone exceeds the budget —
      // otherwise a single very wide pinned column would silently never freeze.
      if (next.size > 0 && run + w > budget) { hitCap = true; break; }
      next.set(key, run);
      run += w;
    }
    setOffsets((prev) => {
      if (prev.size === next.size && [...next].every(([k, v]) => prev.get(k) === v)) return prev;
      return next;
    });
    setCapped(hitCap);
  }, [signature, reservedLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const raf = requestAnimationFrame(measure); // measure after the browser lays out
    const root = scrollRef.current;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure()) : null;
    if (ro && root) ro.observe(root);
    // A dragged column width (useColumnWidths) moves every pin to its right.
    if (ro && root) root.querySelectorAll<HTMLElement>("thead th[data-col-key]").forEach((el) => {
      if (pinnedInOrder.includes(el.dataset.colKey ?? "")) ro.observe(el);
    });
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, deps]);

  const offsetFor = useCallback((colKey: string) => offsets.get(colKey), [offsets]);
  return { scrollRef, offsetFor, capped };
}
